import "./SourceFilesList.scss";
import { useEffect, useRef, useState } from "react";
import SourceFilesListControls from "./SourceFilesListControls";
import SourceFilesListItem from "./SourceFilesListItem";
import { Reorder } from "framer-motion";
import { SourceFile } from "../../../../ImportWizard";
import SourceFilesListSummary from "./SourceFilesListSummary";

export interface SourceFilesListProps {
    sourceFiles: SourceFile[];
    overrides?: { [key: string]: Partial<SourceFile> };
    setSourceFiles: (_: React.SetStateAction<SourceFile[] | undefined>) => void;
    selectedFiles: number[];
    setSelectedFiles: (_: React.SetStateAction<number[]>) => void;
    resetSourceFiles: () => void;
    resetSelectedFiles: () => void;
    errorMsgs: string[];
}

/** Handle logic for selecting file list elements. */
function useHandleSelecting(props: SourceFilesListProps) {
    // Used for deciding pivot point when shift-clicking
    const [lastDirectlySelected, setLastDirectlySelected] = useState(undefined);

    function handleClick(event: React.MouseEvent, index: number) {
        event.preventDefault();

        // By default, replace selected file indexes
        let newSelected = [index];

        // If holding <Ctrl>, append the selected file index (or remove item if already selected)
        if (event.ctrlKey) {
            if (props.selectedFiles.includes(index)) {
                newSelected = props.selectedFiles.filter((i) => i != index);
                if (newSelected.length == 0) {
                    setLastDirectlySelected(undefined);
                }
            } else {
                newSelected = [...props.selectedFiles, index];
                setLastDirectlySelected(index);
            }
        }
        // If holding <Shift>, append all entities between the last selected entity and the current one
        else if (event.shiftKey && props.selectedFiles?.length) {
            newSelected = [];
            const lastIndex = lastDirectlySelected ?? props.selectedFiles[0];
            const start = Math.min(index, lastIndex);
            const end = Math.max(index, lastIndex);

            for (let i = start; i <= end; i++) {
                newSelected.push(i);
            }
        } else {
            setLastDirectlySelected(index);
        }

        props.setSelectedFiles(newSelected);
    }

    function handleDragEnd(index: number) {
        // Only one item should be selected at the end of a drag event
        if (props.selectedFiles.length != 1 || props.selectedFiles[0] != index) {
            props.setSelectedFiles([index]);
        }
        setLastDirectlySelected(index);
    }

    return {
        handleClick,
        handleDragEnd,
    };
}

function SourceFilesList(props: SourceFilesListProps) {
    const { handleClick, handleDragEnd } = useHandleSelecting(props);

    function handleRemoveFile(e: React.MouseEvent, index: number) {
        props.resetSelectedFiles();
        e.preventDefault();
        // Ignore clicking for the parent element
        e.stopPropagation();

        if (props.sourceFiles.length == 1) {
            props.resetSourceFiles();
            return;
        }

        props.setSourceFiles([...props.sourceFiles.slice(0, index), ...props.sourceFiles.slice(index + 1)]);
    }

    function updateSelectedFiles(next: SourceFile[]) {
        props.setSelectedFiles((prev) => {
            const nextSelectedFiles = [];
            for (const i of prev) {
                const sourceFile = props.sourceFiles[i];
                const nextIndex = next.findIndex(
                    (f) => f.file.name == sourceFile.file.name && f.file.lastModified == sourceFile.file.lastModified,
                );
                if (nextIndex != -1) {
                    nextSelectedFiles.push(nextIndex);
                }
            }
            return nextSelectedFiles;
        });
    }

    function onReorder(files: SourceFile[]) {
        updateSelectedFiles(files);
        props.setSourceFiles(files);
    }

    return (
        <section className="gap-2 w-100 d-flex flex-column source-files-list">
            <div className="source-files-list-table">
                <header className="source-files-list-header">
                    <span>#</span>
                    <span>File Name</span>
                    <span>Start Date</span>
                    <span>Length</span>
                    <span>Codec</span>
                    <span>FPS</span>
                    <span>File Size</span>
                </header>

                <div className="source-files-list-body">
                    <Reorder.Group
                        axis="y"
                        layoutScroll
                        values={props.sourceFiles}
                        onReorder={onReorder}
                        className="m-0 list-unstyled"
                    >
                        {(props.sourceFiles ?? []).map((sourceFile, index) => (
                            <SourceFilesListItem
                                key={sourceFile.file.name}
                                sourceFile={sourceFile}
                                overrideStart={props.overrides?.[sourceFile.file.name]?.start.value}
                                updateSourceFile={(nextSourceFile: SourceFile) => {
                                    props.setSourceFiles((prev) => {
                                        const next = [...prev];
                                        next[index] = nextSourceFile;
                                        return next;
                                    });
                                }}
                                index={index}
                                handleClick={handleClick}
                                handleDragEnd={handleDragEnd}
                                handleRemoveFile={handleRemoveFile}
                                {...props}
                            />
                        ))}
                    </Reorder.Group>
                </div>
            </div>

            {/* <SourceFilesListSummary {...props} /> */}

            <SourceFilesListControls {...props} />
        </section>
    );
}

export default SourceFilesList;
